import React, { useState, useEffect } from 'react';
import EmployeeNavigation from '../../layouts/employeeNavigation';
import WelcomeSection from './components/welcomeSection';
import AttendanceCalendar from './components/attendanceCalendar';
import AttendanceHistory from './components/attendanceHistory';
import ProfileCard from './components/profileCard';
import AttendanceStats from './components/attendanceStats';
import { useAuth } from 'components/ui/navigationGuard';
import axiosInstance from 'utils/axiosInstance';

const EmployeeDashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState({});
  const [attendanceData, setAttendanceData] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        
        // Get employee data
        const employee_id = user?.employee_id;
        const employeeResponse = await axiosInstance.get(`/employee/me/${employee_id}`);
        setEmployee(employeeResponse?.data?.data?.employee || {});
        
        // Get today's attendance
        const todayAttendanceResponse = await axiosInstance.get(`/employee/me/today-attendance/${employee_id}`);
        setTodayAttendance(todayAttendanceResponse?.data?.data || {});
        
        // Get attendance calendar data
        const calendarResponse = await axiosInstance.get(`/employee/me/attendance-calendar/${employee_id}`);
        setAttendanceData(calendarResponse?.data?.data?.attendanceData || []);
        
        // Get attendance history
        const historyResponse = await axiosInstance.get(`/employee/me/attendance-history/${employee_id}`);
        setAttendanceRecords(historyResponse?.data?.data?.attendanceRecords || []);
        
        // Get attendance stats
        const statsResponse = await axiosInstance.get(`/employee/me/attendance-stats/${employee_id}`);
        setStats(statsResponse?.data?.data || {});
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      }
    };
    
    if (isAuthenticated && user?.employee_id) {
      loadDashboardData();
    }
  }, [isAuthenticated, user]);

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <EmployeeNavigation />
        <div className="pt-16 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <EmployeeNavigation
        user={user}
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
      />

      <main className={`pt-16 transition-all duration-200 ease-in-out ${isCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <div className="max-w-7xl mx-auto p-6 space-y-6">
          {/* Welcome Section */}
          <div className="mb-8">
            <WelcomeSection employee={employee} todayAttendance={todayAttendance} />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-8 space-y-6">
              {/* Attendance Calendar */}
              <AttendanceCalendar attendanceData={attendanceData} />

              {/* Attendance History */}
              <AttendanceHistory attendanceRecords={attendanceRecords} />
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              {/* Profile Card */}
              <ProfileCard employee={employee} />

              {/* Attendance Statistics */}
              <AttendanceStats stats={stats} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmployeeDashboard;